import React, { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { buildDaySlots, weekdayKey, DAY_LABEL } from "@/lib/scheduler";

const ymd = (d) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; };

// Presentational slot grid. Parent owns `config` + `appointments`; this just
// renders the day's open slots and reports the chosen one (a Date) via onPick.
export function SlotGrid({ config, date, onDateChange, appointments, npi, providerName, selectedStart, onPick }) {
  const slots = useMemo(
    () => buildDaySlots(config, date, appointments, { npi, providerName }),
    [config, date, appointments, npi, providerName],
  );
  const key = date ? weekdayKey(date) : null;
  const hasHours = key && (config?.weekly?.[key] || []).length > 0;
  const openCount = slots.filter((s) => s.available).length;
  const step = (dir) => { const d = new Date(`${date}T00:00:00`); d.setDate(d.getDate() + dir); onDateChange(ymd(d)); };
  const selIso = selectedStart ? new Date(selectedStart).toISOString() : null;

  return (
    <div data-testid="slot-grid">
      <div className="flex items-center gap-2 mb-2.5">
        <button type="button" onClick={() => step(-1)} data-testid="slot-prev-day" className="w-8 h-8 rounded-[8px] border border-black/[.1] grid place-items-center text-[#3F4551] hover:bg-black/[.04]"><ChevronLeft size={15} /></button>
        <input type="date" value={date} onChange={(e) => onDateChange(e.target.value)} data-testid="slot-date"
          className="flex-1 h-9 px-3 rounded-[9px] border border-black/[.14] text-[13px] outline-none" />
        <button type="button" onClick={() => step(1)} data-testid="slot-next-day" className="w-8 h-8 rounded-[8px] border border-black/[.1] grid place-items-center text-[#3F4551] hover:bg-black/[.04]"><ChevronRight size={15} /></button>
      </div>
      <div className="text-[11px] mb-2" style={{ color: "#8A8F99" }}>
        {hasHours ? `${openCount} open slot${openCount === 1 ? "" : "s"} · ${config.slot_minutes}-min` : `${DAY_LABEL[key] || "This day"} is closed for this provider`}
      </div>
      {!hasHours ? (
        <div className="rounded-[10px] border border-dashed border-black/[.14] px-3 py-5 text-center text-[12px]" style={{ color: "#8A8F99" }} data-testid="slot-closed">
          No working hours on {DAY_LABEL[key] || "this day"}. Pick another date.
        </div>
      ) : slots.length === 0 ? (
        <div className="rounded-[10px] border border-dashed border-black/[.14] px-3 py-5 text-center text-[12px]" style={{ color: "#8A8F99" }}>No slots.</div>
      ) : (
        <div className="grid grid-cols-3 gap-2 max-h-[220px] overflow-y-auto pr-0.5" data-testid="slot-options">
          {slots.map((s, i) => {
            const iso = s.start.toISOString();
            const sel = selIso === iso;
            return (
              <button key={iso} type="button" disabled={!s.available} onClick={() => onPick(s.start)}
                data-testid={`slot-${i}`} title={s.reason === "booked" ? "Already booked" : s.reason === "past" ? "In the past" : ""}
                className="h-9 rounded-[9px] text-[12.5px] font-semibold border transition disabled:cursor-not-allowed"
                style={{
                  borderColor: sel ? "#4B4BE8" : s.available ? "rgba(0,0,0,.14)" : "rgba(0,0,0,.06)",
                  background: sel ? "#4B4BE8" : s.available ? "#fff" : "#F4F4F5",
                  color: sel ? "#fff" : s.available ? "#141412" : "#BDBDC2",
                  textDecoration: s.available ? "none" : "line-through",
                  boxShadow: sel ? "0 0 0 3px rgba(75,75,232,.18)" : "none",
                }}>
                {s.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
